import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';

import { useOpen } from 'src/hooks/use-open';

import Icons from 'assets/icons';

interface CollapsibleSectionProps {
  title: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

const CollapsibleSection = ({
  title,
  defaultOpen = false,
  children,
  headerRight,
  className,
  'data-testid': dataTestId,
}: CollapsibleSectionProps): React.JSX.Element => {
  const { isOpen, handleToggle } = useOpen(defaultOpen);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const contentElement = contentRef.current;
    let resizeObserver: ResizeObserver | undefined;

    const updateHeight = (): void => {
      if (contentElement) {
        contentElement.style.height = isOpen
          ? `${contentElement.scrollHeight}px`
          : '0px';
      }
    };

    if (contentElement) {
      resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(contentElement);
    }

    updateHeight();

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [isOpen]);

  return (
    <div
      className={classNames('rounded-lg border border-navy-100', className)}
      data-testid={dataTestId}
    >
      <button
        type="button"
        className={classNames(
          'flex items-center justify-between w-full py-3 px-4 font-bold text-gray-700 gap-3',
          { 'border-b border-navy-100': isOpen }
        )}
        onClick={handleToggle}
        aria-expanded={isOpen}
      >
        <span>{title}</span>
        <span className="flex items-center gap-3">
          {headerRight}
          <Icons.Arrow
            className={classNames('rotate-90 transition shrink-0', {
              '!-rotate-90': isOpen,
            })}
          />
        </span>
      </button>
      <div ref={contentRef} className="overflow-hidden transition-all">
        <div
          className={classNames({
            'p-4': isOpen,
            hidden: !isOpen,
          })}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default CollapsibleSection;
